import * as React from 'react';
import { createContext, useEffect, useState } from 'react';
import { QueueLeadNoteSlider } from '../components/QueueLeadNoteSlider';
import { useQueue } from '../hooks/queue';
import { AidaNoteViewModel } from '../viewModels/note';
import { DialerPhoneCallStatus, useTelephony } from './telephony';
import { useTysonMode } from './tysonMode';

export interface IQueueLeadNoteContext {
	defaultNote: AidaNoteViewModel;
	setDefaultNote: React.Dispatch<React.SetStateAction<AidaNoteViewModel>>;
	showQueueLeadNote: boolean;
	setShowQueueLeadNote: React.Dispatch<React.SetStateAction<boolean>>;
	forceClose: boolean;
	setForceClose: React.Dispatch<React.SetStateAction<boolean>>;
}

export const QueueLeadNoteContext = createContext<IQueueLeadNoteContext>(null);

export const QueueLeadNoteProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const queue = useQueue();
	const [showQueueLeadNote, setShowQueueLeadNote] = useState(false);
	const [defaultNote, setDefaultNote] = useState<AidaNoteViewModel>(null);
	const [forceClose, setForceClose] = useState(false);
	const tysonMode = useTysonMode();

	useEffect(() => {
		setShowQueueLeadNote(!!defaultNote);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultNote]);

	const { phoneCalls, phoneCall, phoneCallStatus, isPendingOutcome } = useTelephony();
	useEffect(() => {
		// Show call outcome UI when there's a pending outcome and no active call in progress.
		// This covers:
		// 1. Landing on page with pending outcome from API (no Twilio call active)
		// 2. After a Twilio call ends (phoneCallStatus goes back to NoCall)
		// We check phoneCallStatus instead of phoneCalls.currentCall because currentCall
		// stays set until outcome is saved, but phoneCallStatus reflects active Twilio state.
		if (isPendingOutcome && phoneCallStatus === DialerPhoneCallStatus.NoCall && !forceClose) {
			setShowQueueLeadNote(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phoneCallStatus, isPendingOutcome, forceClose]);

	// only show editor if there is a company loaded and you are not in a call with a different company
	const isConnected = !!phoneCall && phoneCallStatus !== DialerPhoneCallStatus.Ringing;
	const isDifferentCompany = isConnected && (phoneCall.companyId !== queue.companyId || !phoneCall.companyId);
	const showNoteEditor = !!queue.companyId && !isDifferentCompany;

	// Reset forceClose when company changes
	useEffect(() => {
		setForceClose(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queue.companyId]);

	// Reset forceClose when call is cleared
	useEffect(() => {
		if (!phoneCalls.currentCall) {
			setForceClose(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phoneCalls.currentCall]);

	return (
		<QueueLeadNoteContext.Provider
			value={{
				defaultNote,
				setDefaultNote,
				setShowQueueLeadNote,
				showQueueLeadNote,
				forceClose,
				setForceClose,
			}}
		>
			{children}
			{showNoteEditor && !tysonMode.isActive && (
				<QueueLeadNoteSlider
					defaultNote={defaultNote}
					isExpanded={showQueueLeadNote}
					toggleExpand={setShowQueueLeadNote}
				/>
			)}
		</QueueLeadNoteContext.Provider>
	);
};

export const useQueueLeadNote = () => {
	return React.useContext<IQueueLeadNoteContext>(QueueLeadNoteContext);
};
